import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useBoolean } from 'react-use'

import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Field, Label, ModalGrid, SimpleInput, Tooltip } from '@makyoapp/makyo-ui'

import { useReqError } from '@/hooks/useReqError'
import { validateTableName } from '@/shared/ag-grid'
import { useDebounceInput, useFormEnter } from '@/shared/makyo-hooks'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

interface ModalEditTableProps {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
}

export function ModalEditTable({ show, setShow }: ModalEditTableProps) {
  const { table, handleRenameTable, tableList } = useGridV2Context()
  const { t } = useTranslation()

  const [error, setError] = useState('')
  const [hasError, setHasError] = useBoolean(false)

  const printAPIError = useReqError()

  const [debounceInput, cancelDebounceInput, setDebounceInput, value, setValue] = useDebounceInput(
    table ? table.name : ''
  )
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  // Reset form when modal is shown
  useEffect(() => {
    if (show) {
      setValue(table?.name || '')
      setError('')
      setHasError(false)
    }
  }, [show, table?.name, setValue, setHasError])

  const reset = useCallback(() => {
    setIsBtnLoading(false)
    setShow(false)
    setError('')
    setHasError(false)
    cancelDebounceInput()
    setDebounceInput('')
    setValue('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelDebounceInput, setDebounceInput, setValue, setIsBtnLoading, setHasError])

  const handleSubmit = () => {
    if (!table) return

    if (!value.trim()) {
      setError('Table name is required')
      setHasError(true)
      return
    }
    if (value && !hasError) {
      cancelDebounceInput()
      setIsBtnLoading(true)
      handleRenameTable(table.id, value)
        .then(() => {
          toast.success(t('Table name updated'))
        })
        .catch(printAPIError)
        .finally(() => {
          reset()
        })
    }
  }

  const formId = 'modal-edit-table'

  useFormEnter(formId, () => {
    handleSubmit()
  })

  const handleCancel = () => {
    reset()
    setShow(false)
  }

  if (!table) return null

  return (
    <ModalGrid
      show={show}
      onCancel={handleCancel}
      id={`${table.id}-modal`}
      title={table.isSnapshot ? t('Rename Snapshot') : table.isQuery ? t('Rename Query') : t('Rename Table')}
      isLoading={isBtnLoading}
      disabled={hasError}
      onConfirm={handleSubmit}
      onHide={handleCancel}
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <Field>
          <Label required>
            {t('Name')}
            <Tooltip content={<div className='max-w-xs text-m-sm p-1.5'>{t('Valid letters')}</div>}>
              <span className='ml-1 inline-flex align-middle text-light cursor-help'>
                <MaterialIcon icon='info' size={14} />
              </span>
            </Tooltip>
          </Label>
          <SimpleInput
            error={error ? `${t(error)}. ${t('Valid letters')}` : ''}
            autoFocus
            required
            value={value}
            onChange={(e) => {
              const val = e.currentTarget.value
              const tablesToCheck = tableList.filter((tb) => tb.id !== table.id)
              const errMsg = validateTableName(val, tablesToCheck)
              setError(errMsg)
              setHasError(errMsg !== '')
              setValue(val)
            }}
          />
        </Field>
      </form>
    </ModalGrid>
  )
}
